import { useEffect, useMemo, useRef, useState } from "react"

import { useEvent } from "shared/lib/useEvent"

import ResizeObserver from "resize-observer-polyfill"

export function useTabBarScrollState({
  scrollDistance
}: {
  scrollDistance: number
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollGroupRef = useRef<HTMLDivElement>(null)

  const [scrollLeft, setScrollLeft] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [contentWidth, setContentWidth] = useState(0)
  const [scrollGroupWidth, setScrollGroupWidth] = useState(0)

  const hasStartShadow = scrollLeft > 0
  const hasEndShadow = useMemo(() => {
    return (
      Math.floor(scrollLeft) + Math.floor(containerWidth) <
      Math.floor(contentWidth)
    )
  }, [scrollLeft, containerWidth, contentWidth])

  // Check if content overflows the full scroll-group width
  // This prevents arrows from showing when they themselves cause overflow
  const hasOverflow = useMemo(() => {
    return contentWidth > scrollGroupWidth
  }, [contentWidth, scrollGroupWidth])

  const canScrollLeft = hasStartShadow
  const canScrollRight = hasEndShadow

  const measure = useEvent(() => {
    if (!scrollRef.current || !contentRef.current) return
    setContainerWidth(scrollRef.current.getBoundingClientRect().width)
    setContentWidth(contentRef.current.getBoundingClientRect().width)
    setScrollLeft(scrollRef.current.scrollLeft)

    // Measure scroll-group width (full available space)
    if (scrollGroupRef.current) {
      setScrollGroupWidth(scrollGroupRef.current.getBoundingClientRect().width)
    }
  })

  const scrollLeftAction = useEvent(() => {
    scrollRef.current?.scrollBy({ left: -scrollDistance, behavior: "smooth" })
  })

  const scrollRightAction = useEvent(() => {
    scrollRef.current?.scrollBy({ left: scrollDistance, behavior: "smooth" })
  })

  const scrollToTab = useEvent((tabElement: HTMLElement) => {
    tabElement.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest"
    })
  })

  useEffect(() => {
    const observer = new ResizeObserver(measure)
    if (scrollRef.current) observer.observe(scrollRef.current)
    if (contentRef.current) observer.observe(contentRef.current)
    if (scrollGroupRef.current) observer.observe(scrollGroupRef.current)
    scrollRef.current?.addEventListener("scroll", measure)
    return () => {
      scrollRef.current?.removeEventListener("scroll", measure)
      observer.disconnect()
    }
  }, [measure])

  return {
    scrollRef,
    contentRef,
    scrollGroupRef,
    canScrollLeft,
    canScrollRight,
    hasStartShadow,
    hasEndShadow,
    hasOverflow,
    scrollLeft: scrollLeftAction,
    scrollRight: scrollRightAction,
    scrollToTab
  }
}
